import {Component, computed, inject, model} from '@angular/core';
import {MatDialogRef} from "@angular/material/dialog";
import {httpResource} from "@angular/common/http";
import {IErrorLogDetails} from "../../interfaces/admin-panel.interface";
import {SharedModule} from "../../../shared/shared.module";
import {MatButton} from "@angular/material/button";
import {MatCard, MatCardContent} from "@angular/material/card";
import {MatGridList, MatGridTile} from "@angular/material/grid-list";
import {MatTooltip} from "@angular/material/tooltip";
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatIcon} from "@angular/material/icon";
import {DomSanitizer} from "@angular/platform-browser";
import moment from 'moment';

@Component({
  selector: 'app-error-logs-details-modal',
  imports: [
    SharedModule,
    MatButton,
    MatCard,
    MatCardContent,
    MatGridList,
    MatGridTile,
    MatTooltip,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    MatIcon
  ],
  templateUrl: './error-logs-details-modal.component.html',
  styles: [`
    .detail-textarea {
      resize: none;
    }
  `]
})
export class ErrorLogsDetailsModalComponent {
  private readonly dialogRef = inject(MatDialogRef<ErrorLogsDetailsModalComponent>);

  private readonly sanitizer = inject(DomSanitizer);

  public readonly id = model.required<number>();

  private readonly errorLogsDetailsResource = httpResource<IErrorLogDetails>(
    () => `dbov2/error-logs/${this.id()}`
  );

  protected readonly details = computed(() => {
    const value = this.errorLogsDetailsResource.value();
    if (!value) return value;
    return {
      ...value,
      timestamp: value.timestamp ? moment(value.timestamp).format('MM/DD/YYYY') : value.timestamp
    };
  });

  protected readonly payloadDetails = computed(() => {
    const response = this.errorLogsDetailsResource.value();
    if (!response || !response.payloadDetails?.detail) return;
    const responseDetail = response.payloadDetails.detail;
    const detail =
      responseDetail.includes('html') ? this.sanitizer.bypassSecurityTrustHtml(responseDetail) : responseDetail;
    const payloadDetailsData = Object.keys(response.payloadDetails)
      .filter(key => key !== 'status' && key !== 'detail')
      .map(key => {
        let value = response.payloadDetails![key];
        if (key === 'date-time' && typeof value === 'string') {
          const m = moment(value);
          if (m.isValid()) {
            value = m.format('MM-DD-YYYY HH:mm:ss');
          }
        }
        return {label: this.formatLabel(key), value}
      });
    return {...response.payloadDetails, detail, payloadDetailsData};
  });

  protected close(): void {
    this.dialogRef.close();
  }

  protected getDetailText(): string {
    const response = this.errorLogsDetailsResource.value();
    if (!response?.payloadDetails?.detail) return '';
    const detail = response.payloadDetails.detail;
    if (detail.includes('<') && detail.includes('>')) {
      const temp = document.createElement('div');
      temp.innerHTML = detail;
      const titleEl = temp.querySelector('title');
      if (titleEl) {
        titleEl.remove();
      }
      return (temp.textContent || temp.innerText || detail).trim();
    }
    return detail;
  }

  private formatLabel(label: string): string {
    const labelMap: { [key: string]: string } = {
      'type': 'Type',
      'title': 'Title',
      'traceId': 'Trace Id',
      'date-time': 'Date-time',
      'environment': 'Environment',
      'correlationId': 'Correlation Id',
      'method': 'Method',
      'protocol': 'Protocol',
      'host': 'Host',
      'path': 'Path'
    };
    
    return labelMap[label] || label.charAt(0).toUpperCase() + label.slice(1);
  }
}
